import React from 'react';
import { formatEther } from 'viem';
import { NetworkIndicator } from '@components/Song/MintComponents/NetworkIndicator';
import { PriceDropdown } from '@components/modals/Buy/shared';
import { CollectSoundSwapEthConversionDocument, useQuery } from '@graphql/reactQuery';
import { formatWeiToEthDisplayFree } from '@utils/helpers';
import type { SelectedMintPanel } from './CheckoutModal';
import { type ScheduleWithTierInfo, useTiersMintPrice } from './data';

export const TiersPriceBreakdown = ({
  schedule,
  quantity,
  chainId,
  editionAddress,
  selectedMintPanel,
}: {
  schedule: ScheduleWithTierInfo;
  quantity: number;
  chainId: number;
  editionAddress: string;
  selectedMintPanel: SelectedMintPanel;
}) => {
  const { data: ethToUsdData } = useQuery(CollectSoundSwapEthConversionDocument, {
    filterQueryKey: {
      key: 'EthToUsd',
    },
    staleTime: 0,
  });

  const ethToUsdValue = ethToUsdData?.data.currencies.ethToUsd || 0;
  const basePrice = schedule?.price || 0n;
  const basePriceEthString = formatWeiToEthDisplayFree({
    weiValue: basePrice,
    maxDecimals: 6,
  });

  const { totalMintPriceWei } = useTiersMintPrice({
    editionAddress,
    mintSchedule: schedule,
    chainId,
    quantity,
  });

  // This component should already be handing null state
  const totalFees = totalMintPriceWei?.fees ?? 0n;

  // we don't show at all if zero
  const totalFeesEthString = formatWeiToEthDisplayFree({
    weiValue: totalFees,
    maxDecimals: 6,
  });

  const totalBeforeFeesWei = totalMintPriceWei?.subTotal ?? 0n;
  const totalBeforeFeesEthString = formatWeiToEthDisplayFree({
    weiValue: totalBeforeFeesWei,
    maxDecimals: 6,
  });

  const totalPriceEthString = formatWeiToEthDisplayFree({
    weiValue: totalMintPriceWei?.total ?? 0n,
    maxDecimals: 6,
  });

  const totalPriceEth = formatEther(totalMintPriceWei?.total ?? 0n);
  const totalUsdPrice = (ethToUsdValue * Number(totalPriceEth)).toFixed(2);

  const isLoading = totalMintPriceWei == null;
  const totalPriceWei = totalMintPriceWei?.total ?? 0n;
  const isGAEdition = selectedMintPanel === 'GA_PUBLIC';

  return (
    <div className="flex w-full flex-col gap-2">
      <PriceDropdown
        chainId={chainId}
        totalPriceEthString={totalPriceEthString}
        totalUsdPrice={totalUsdPrice}
        basePriceEthString={basePriceEthString}
        quantity={quantity}
        totalBeforeFeesEthString={totalBeforeFeesEthString}
        totalFees={totalFees}
        totalFeesEthString={totalFeesEthString}
        totalPriceWei={totalMintPriceWei?.total ?? 0n}
        isLoading={totalMintPriceWei == null}
        isGA={isGAEdition}
      />

      {isGAEdition && (
        <div className="flex min-h-[40px] w-full justify-between">
          <h2 className="font-title text-title-m font-medium">Total</h2>
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-1">
              <NetworkIndicator chainId={chainId} size="sm" />
              <h2 className="font-title text-title-m font-medium">
                {isLoading ? '...' : totalPriceEthString}
              </h2>
            </div>
            {!isLoading && totalPriceWei !== 0n && (
              <p className="text-right text-base-m text-base400">~${totalUsdPrice}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
